import { supabase } from '../lib/supabase.js';

export class CommunicationTemplateManager {
  constructor() {
    this.templates = [];
    this.isLoading = false;
    this.editingTemplate = null;
    
    // Status triggers available for communication templates
    this.statusTriggers = [
      { value: 'pending_pickup', label: 'Pending Pickup' },
      { value: 'driver_dispatched_pickup', label: 'Driver Dispatched (Pickup)' },
      { value: 'picked_up', label: 'Picked Up' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'ironing_complete', label: 'Ironing Complete' },
      { value: 'ready_for_delivery', label: 'Ready for Delivery' },
      { value: 'driver_dispatched_delivery', label: 'Driver Dispatched (Delivery)' },
      { value: 'out_for_delivery', label: 'Out for Delivery' },
      { value: 'completed', label: 'Completed' },
      { value: 'payment_failed', label: 'Payment Failed' },
      { value: 'payment_canceled', label: 'Payment Canceled' }
    ];
  }

  render() {
    return `
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-bold text-gray-900">Communication Templates</h2>
          <button 
            id="add-template-btn" 
            class="bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700"
          >
            Add Template
          </button>
        </div>

        <!-- Template Form -->
        <div id="template-form" class="hidden mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h3 class="font-medium text-gray-900 mb-4" id="form-title">Add New Template</h3>
          
          <form id="template-form-element" class="space-y-4">
            <input type="hidden" id="template-id">
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input 
                  type="text" 
                  id="template-name" 
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="e.g., Pickup Confirmation SMS"
                >
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select 
                  id="template-type" 
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="">Select type...</option>
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </select>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Status Trigger
              </label>
              <select 
                id="template-status-trigger" 
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">Select trigger...</option>
                ${this.statusTriggers.map(trigger => 
                  `<option value="${trigger.value}">${trigger.label}</option>`
                ).join('')}
              </select>
            </div>

            <div id="subject-field" class="hidden">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Subject (Email only)
              </label>
              <input 
                type="text" 
                id="template-subject"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder="e.g., Order Confirmed - {{order_number}}"
              >
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Message Body
              </label>
              <textarea 
                id="template-body" 
                required
                rows="6"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder="Enter your message template here. Use placeholders like {{customer_name}}, {{order_number}}, etc."
              ></textarea>
            </div>

            <div class="bg-violet-50 p-3 rounded-md">
              <h4 class="font-medium text-violet-900 mb-2">Available Placeholders:</h4>
              <div class="text-sm text-violet-800 grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>{{customer_name}}</div>
                <div>{{order_number}}</div>
                <div>{{total_amount}}</div>
                <div>{{pickup_date}}</div>
                <div>{{pickup_time_slot}}</div>
                <div>{{delivery_date}}</div>
                <div>{{delivery_time_slot}}</div>
                <div>{{pickup_code}}</div>
                <div>{{delivery_code}}</div>
              </div>
            </div>

            <div class="flex space-x-3">
              <button 
                type="submit" 
                class="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
              >
                Save Template
              </button>
              <button 
                type="button" 
                id="cancel-form-btn"
                class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        <!-- Templates List -->
        <div id="templates-list">
          <div class="text-center py-8 text-gray-500">
            Loading templates...
          </div>
        </div>
      </div>
    `;
  }

  async bindEvents() {
    await this.loadTemplates();

    // Add template button
    document.getElementById('add-template-btn').addEventListener('click', () => {
      this.showForm();
    });

    // Cancel form button
    document.getElementById('cancel-form-btn').addEventListener('click', () => {
      this.hideForm();
    });

    // Template type change
    document.getElementById('template-type').addEventListener('change', (e) => {
      const subjectField = document.getElementById('subject-field');
      if (e.target.value === 'email') {
        subjectField.classList.remove('hidden');
        document.getElementById('template-subject').required = true;
      } else {
        subjectField.classList.add('hidden');
        document.getElementById('template-subject').required = false;
      }
    });

    // Form submission
    document.getElementById('template-form-element').addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.saveTemplate();
    });
  }

  async loadTemplates() {
    try {
      const { data: templates, error } = await supabase
        .from('communication_templates')
        .select('*')
        .order('status_trigger', { ascending: true })
        .order('type', { ascending: true });

      if (error) throw error;

      this.templates = templates;
      this.renderTemplatesList();
    } catch (error) {
      this.showError('Error loading templates: ' + error.message);
    }
  }

  renderTemplatesList() {
    const listDiv = document.getElementById('templates-list');
    
    if (this.templates.length === 0) {
      listDiv.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          No templates found. Click "Add Template" to create your first template.
        </div>
      `;
      return;
    }

    // Group templates by status trigger
    const groupedTemplates = this.templates.reduce((acc, template) => {
      if (!acc[template.status_trigger]) {
        acc[template.status_trigger] = [];
      }
      acc[template.status_trigger].push(template);
      return acc;
    }, {});

    listDiv.innerHTML = Object.entries(groupedTemplates).map(([trigger, templates]) => {
      const triggerLabel = this.statusTriggers.find(t => t.value === trigger)?.label || trigger;
      
      return `
        <div class="mb-6">
          <h3 class="font-medium text-gray-900 mb-3 pb-2 border-b border-gray-200">
            ${triggerLabel}
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            ${templates.map(template => this.renderTemplateCard(template)).join('')}
          </div>
        </div>
      `;
    }).join('');

    // Bind edit and delete events
    this.bindTemplateEvents();
  }

  renderTemplateCard(template) {
    return `
      <div class="border border-gray-200 rounded-md p-4 hover:shadow-md transition-shadow">
        <div class="flex justify-between items-start mb-2">
          <h4 class="font-medium text-gray-900">${template.name}</h4>
          <span class="text-xs px-2 py-1 rounded-full ${
            template.type === 'email' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
          }">
            ${template.type.toUpperCase()}
          </span>
        </div>
        
        ${template.subject ? `
          <div class="text-sm text-gray-600 mb-2">
            <strong>Subject:</strong> ${template.subject}
          </div>
        ` : ''}
        
        <div class="text-sm text-gray-600 mb-3">
          <strong>Body:</strong> ${template.body.substring(0, 100)}${template.body.length > 100 ? '...' : ''}
        </div>
        
        <div class="flex space-x-2">
          <button 
            class="edit-template-btn text-blue-600 hover:text-blue-800 text-sm"
            data-template-id="${template.id}"
          >
            Edit
          </button>
          <button 
            class="delete-template-btn text-red-600 hover:text-red-800 text-sm"
            data-template-id="${template.id}"
          >
            Delete
          </button>
        </div>
      </div>
    `;
  }

  bindTemplateEvents() {
    // Edit buttons
    document.querySelectorAll('.edit-template-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const templateId = e.target.dataset.templateId;
        this.editTemplate(templateId);
      });
    });

    // Delete buttons
    document.querySelectorAll('.delete-template-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const templateId = e.target.dataset.templateId;
        this.deleteTemplate(templateId);
      });
    });
  }

  showForm(template = null) {
    const form = document.getElementById('template-form');
    const title = document.getElementById('form-title');
    
    form.classList.remove('hidden');
    
    if (template) {
      title.textContent = 'Edit Template';
      this.populateForm(template);
    } else {
      title.textContent = 'Add New Template';
      this.clearForm();
    }
  }

  hideForm() {
    document.getElementById('template-form').classList.add('hidden');
    this.clearForm();
    this.editingTemplate = null;
  }

  populateForm(template) {
    document.getElementById('template-id').value = template.id;
    document.getElementById('template-name').value = template.name;
    document.getElementById('template-type').value = template.type;
    document.getElementById('template-status-trigger').value = template.status_trigger;
    document.getElementById('template-subject').value = template.subject || '';
    document.getElementById('template-body').value = template.body;

    // Show/hide subject field based on type
    const subjectField = document.getElementById('subject-field');
    if (template.type === 'email') {
      subjectField.classList.remove('hidden');
      document.getElementById('template-subject').required = true;
    } else {
      subjectField.classList.add('hidden');
      document.getElementById('template-subject').required = false;
    }

    this.editingTemplate = template;
  }

  clearForm() {
    document.getElementById('template-form-element').reset();
    document.getElementById('template-id').value = '';
    document.getElementById('subject-field').classList.add('hidden');
    document.getElementById('template-subject').required = false;
  }

  async saveTemplate() {
    const templateData = {
      name: document.getElementById('template-name').value,
      type: document.getElementById('template-type').value,
      status_trigger: document.getElementById('template-status-trigger').value,
      subject: document.getElementById('template-subject').value || null,
      body: document.getElementById('template-body').value
    };

    try {
      const templateId = document.getElementById('template-id').value;
      
      if (templateId) {
        // Update existing template
        const { error } = await supabase
          .from('communication_templates')
          .update(templateData)
          .eq('id', templateId);

        if (error) throw error;
      } else {
        // Create new template
        const { error } = await supabase
          .from('communication_templates')
          .insert([templateData]);

        if (error) throw error;
      }

      await this.loadTemplates();
      this.hideForm();
      this.showSuccess(templateId ? 'Template updated successfully' : 'Template created successfully');
    } catch (error) {
      this.showError('Error saving template: ' + error.message);
    }
  }

  editTemplate(templateId) {
    const template = this.templates.find(t => t.id === templateId);
    if (template) {
      this.showForm(template);
    }
  }

  async deleteTemplate(templateId) {
    if (!confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('communication_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;

      await this.loadTemplates();
      this.showSuccess('Template deleted successfully');
    } catch (error) {
      this.showError('Error deleting template: ' + error.message);
    }
  }

  showSuccess(message) {
    // You can implement a toast notification system here
    console.log('Success:', message);
  }

  showError(message) {
    // You can implement a toast notification system here
    console.error('Error:', message);
  }
}